import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { Article } from '../types';
import { findAndLoadArticlesFromDirectory, loadArticleFromPath, createArticleFromMetadata } from '../utils/articleLoader';
import { METADATA_URL } from '../constants/api';

interface ArticleContextType {
  articles: Article[];
  loading: boolean;
  error: string | null;
  refreshArticles: () => Promise<void>;
  loadArticleBySlug: (slug: string) => Promise<Article | null>;
  loadMoreArticles: () => Promise<void>;
  hasMore: boolean;
}

// コンテキストの作成
const ArticleContext = createContext<ArticleContextType>({
  articles: [],
  loading: false,
  error: null,
  refreshArticles: async () => {},
  loadArticleBySlug: async () => null,
  loadMoreArticles: async () => {},
  hasMore: false
});

// カスタムフックの作成
export const useArticles = () => useContext(ArticleContext);

interface ArticleProviderProps {
  children: React.ReactNode;
  directory?: string;
  metadataPath?: string;
}

interface ArticleMetadata {
  id: string;
  title: string;
  created_at: string;
  category: string;
  subcategory: string;
  tags: {
    id: string;
    name: string;
    color: string;
  }[];
  excerpt: string;
  slug: string;
  file_path: string;
  directory: string;
  has_cover_image?: boolean;
  is_top?: boolean;
}

export const ArticleProvider: React.FC<ArticleProviderProps> = ({ 
  children, 
  directory = 'articles',
  metadataPath = 'articles/metadata.json'
}) => {
  const [articles, setArticles] = useState<Article[]>([]);
  const [articlesMetadata, setArticlesMetadata] = useState<ArticleMetadata[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState<number>(1);
  const [hasMore, setHasMore] = useState<boolean>(true);
  const PAGE_SIZE = 6;

  // 記事メタデータを読み込む関数
  const loadArticlesMetadata = useCallback(async (): Promise<ArticleMetadata[]> => {
    try {
      const response = await fetch(METADATA_URL);
      
      if (!response.ok) {
        throw new Error(`メタデータの取得に失敗しました (${response.status})`);
      }
      
      const metadata = await response.json();
      return metadata;
    } catch (err) {
      console.error('Error loading metadata:', err);
      throw err;
    }
  }, []);

  // 記事を読み込む関数 - useCallbackでラップ
  const loadArticles = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      // 記事メタデータを読み込む
      const metadata = await loadArticlesMetadata();
      setArticlesMetadata(metadata);
      
      // sort metadata by creation date in descending order
      metadata.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());

      // generate all articles from sorted metadata
      const articlesFromMetadata = metadata.map(createArticleFromMetadata);
      setArticles(articlesFromMetadata);
      setHasMore(false);
    } catch (err: any) {
      console.error('Error loading articles:', err);
      setError(`記事の読み込みに失敗しました: ${err?.message || String(err)}`);
      setArticles([]);
    } finally {
      setLoading(false);
    }
  }, [directory, metadataPath, loadArticlesMetadata]);

  // 追加の記事を読み込む関数
  const loadMoreArticles = async () => {
    if (loading || !hasMore) return;

    setLoading(true);
    const nextPage = page + 1;
    const start = (nextPage - 1) * PAGE_SIZE;
    const end = start + PAGE_SIZE;

    try {
      // メタデータから記事情報を生成（記事本文を読み込まない）
      const newArticles = articlesMetadata
        .slice(start, end)
        .map(createArticleFromMetadata);

      if (newArticles.length > 0) {
        // 記事を日付順にソート（新しい順）
        newArticles.sort((a, b) => {
          const dateA = new Date(a.date).getTime();
          const dateB = new Date(b.date).getTime();
          return dateB - dateA;
        });
        
        setArticles(prevArticles => [...prevArticles, ...newArticles]);
        setPage(nextPage);
        setHasMore(newArticles.length === PAGE_SIZE && (start + newArticles.length) < articlesMetadata.length);
      } else {
        setHasMore(false);
      }
    } catch (err) {
      console.error('Error loading more articles:', err);
    } finally {
      setLoading(false);
    }
  };

  // スラッグから特定の記事を読み込む関数
  const loadArticleBySlug = async (slug: string): Promise<Article | null> => {
    try {
      // すでに読み込まれている記事から検索
      const existingArticle = articles.find(article => article.slug === slug);
      if (existingArticle && existingArticle.content) {
        return existingArticle;
      }

      // メタデータから一致する記事を検索
      const metadata = articlesMetadata.find(m => m.slug === slug || m.id === slug);
      if (!metadata) {
        console.warn(`Article with slug "${slug}" not found in metadata`);
        return null;
      }

      // 記事のディレクトリIDを取得（メタデータのdirectoryフィールドまたはid）
      const directoryId = metadata.directory || metadata.id;

      // 記事のHTMLファイルを読み込む
      const articlePath = directory.startsWith('/') 
        ? `${directory}/${directoryId}/main.html` 
        : `/${directory}/${directoryId}/main.html`;
              
      try {
        // 記事データを直接取得（メタデータも渡す）
        const article = await loadArticleFromPath(articlePath, slug, metadata);
        if (article) {
          // タグから国名を取得（すべてのタグは国名）
          const countryNames = metadata.tags?.map(tag => tag.name) || [];
          
          // メタデータの情報で補完
          return {
            ...article,
            id: metadata.id,
            slug: metadata.slug,
            category: metadata.category || article.category,
            subCategory: Array.isArray(metadata.subcategory) ? metadata.subcategory : article.subCategory,
            excerpt: metadata.excerpt || article.excerpt,
            tags: countryNames // すべてのタグは国名
          };
        }
      } catch (err) {
        console.error(`Failed to load article ${slug}:`, err);
      }

      // 記事の読み込みに失敗した場合は最低限の情報を返す
      // すべてのタグを国名として取得
      const countryNames = metadata.tags?.map(tag => tag.name) || [];
      
      return {
        id: metadata.id,
        title: metadata.title,
        slug: metadata.slug || metadata.id,
        date: metadata.created_at || new Date().toISOString().split('T')[0],
        category: metadata.category || 'その他',
        subCategory: Array.isArray(metadata.subcategory) ? metadata.subcategory : [],
        content: '<p>コンテンツを読み込めませんでした。</p>',
        excerpt: metadata.excerpt || '',
        thumbnail: '',
        tags: countryNames // すべてのタグは国名
      };
    } catch (err) {
      console.error(`Error loading article by slug "${slug}":`, err);
      return null;
    }
  };

  // 記事を再読み込みする関数
  const refreshArticles = async () => {
    setPage(1);
    await loadArticles();
  };

  // 初回マウント時に記事を読み込む
  useEffect(() => {
    loadArticles();
  }, [loadArticles]);

  return (
    <ArticleContext.Provider value={{
      articles,
      loading,
      error,
      refreshArticles,
      loadArticleBySlug,
      loadMoreArticles,
      hasMore
    }}>
      {children}
    </ArticleContext.Provider>
  );
}; 